import React, { useCallback, useState } from "react"

import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import { selectProjectWritePermissions } from "../../../Project/helpers/project-permissions.selectors.ts"
import { instanceKeyOf } from "../../../Project/helpers/repeated-project-type.ts"
import { ProjectViewMilestonesGantt } from "../../../Project/ProjectViewPage/components/sections/ProjectViewMilestones/ProjectViewMilestones/ProjectViewMilestonesGantt.tsx"
import { ProjectViewMilestonesHeader } from "../../../Project/ProjectViewPage/components/sections/ProjectViewMilestones/ProjectViewMilestones/ProjectViewMilestonesHeader/ProjectViewMilestonesHeader.tsx"
import { ProjectViewMilestonesList } from "../../../Project/ProjectViewPage/components/sections/ProjectViewMilestones/ProjectViewMilestones/ProjectViewMilestonesList.tsx"
import { type ProjectMilestone, type ProjectMilestoneFinanceItem } from "../../../Project/types/project-phases.types.tsx"
import {
  TOUR_DEMO_DEPENDENCIES,
  TOUR_DEMO_MILESTONES,
  TOUR_DEMO_PROJECT,
  TOUR_DEMO_PROJECT_ID
} from "../../content/tour-demo-data.ts"

type MilestonesView = "table" | "gantt"

/**
 * The REAL Project → Milestones UI (`ProjectViewMilestonesHeader` +
 * `ProjectViewMilestonesList`/`ProjectViewMilestonesGantt`, the exact same
 * components a real project renders), fed local demo state instead of
 * Redux. Every one of those is presentational already — confirmed by
 * reading them directly, no `useSelector`/`connect` inside — so nothing
 * about the table, its columns, the Table/Gantt toggle or the expandable
 * financial breakdown is reimplemented here; only where the data comes from
 * changes. Mutating callbacks update this component's own `useState` and
 * never dispatch a thunk or call the API, so nothing here can ever persist
 * to a real account. `persistColumnConfig={false}` on the list below covers
 * the one persistence path the real component itself owns — column
 * drag/resize/reset, which would otherwise write to the same
 * `TABLE_SURFACES.milestones` key every real project's table uses.
 * `projectPermissions` is the signed-in user's own real
 * permissions (`selectProjectWritePermissions`, read-only) — a user without
 * milestone-edit access sees the same restricted demo a real project would
 * show them.
 */
export const DemoProjectMilestonesSection = () => {
  const userId = useAppSelector((state) => state.auth.userInfo?.id ?? "demo-user")
  const projectPermissions = useAppSelector(selectProjectWritePermissions)

  const [milestones, setMilestones] = useState(TOUR_DEMO_MILESTONES)
  const [view, setView] = useState<MilestonesView>("table")

  const projectTypeDetail = TOUR_DEMO_PROJECT.project_type_details[0]
  const activeTabKey = projectTypeDetail ? instanceKeyOf(projectTypeDetail) : ""

  const onAddMilestoneFinanceItem = useCallback(
    async (data: Partial<ProjectMilestoneFinanceItem>, target: ProjectMilestone) => {
      setMilestones((prev) =>
        prev.map((m) =>
          m.id === target.id
            ? {
                ...m,
                financial_items: [
                  ...m.financial_items,
                  {
                    id: `demo-fi-${Date.now()}`,
                    name: "New financial item",
                    item_type_display: data.item_type === 2 ? "Payable" : "Receivable",
                    comment: "",
                    responsible_type: 1,
                    responsible_type_display: "Customer",
                    status: 0,
                    status_display: "Planned",
                    date: new Date().toISOString().slice(0, 10),
                    due_date: null,
                    project: TOUR_DEMO_PROJECT_ID,
                    project_name: TOUR_DEMO_PROJECT.lead_name,
                    project_phase: m.id,
                    project_phase_name: m.name,
                    assignee: null,
                    attachments: [],
                    created: new Date().toISOString(),
                    updated: new Date().toISOString(),
                    value: "0.00",
                    item_type: 1,
                    ...data
                  } as ProjectMilestoneFinanceItem
                ]
              }
            : m
        )
      )
      return true
    },
    []
  )

  const onUpdateMilestoneFinanceItem = useCallback(
    async (
      itemId: string,
      data: Partial<ProjectMilestoneFinanceItem>,
      target: ProjectMilestone
    ) => {
      setMilestones((prev) =>
        prev.map((m) =>
          m.id === target.id
            ? {
                ...m,
                financial_items: m.financial_items.map((item) =>
                  item.id === itemId ? { ...item, ...data } : item
                )
              }
            : m
        )
      )
    },
    []
  )

  const onDeleteMilestoneFinanceItem = useCallback(
    async (itemId: string, target: ProjectMilestone) => {
      setMilestones((prev) =>
        prev.map((m) =>
          m.id === target.id
            ? { ...m, financial_items: m.financial_items.filter((item) => item.id !== itemId) }
            : m
        )
      )
    },
    []
  )

  const noop = useCallback(() => {}, [])
  const noopAsync = useCallback(async () => {}, [])
  const noopAsyncFalse = useCallback(async () => false, [])
  const noopBulk = useCallback(
    async () => ({ requested: 0, succeeded: [], failed: [] }),
    []
  )

  return (
    <div data-guide-target="project-milestones-panel">
      <ProjectViewMilestonesHeader
        project={TOUR_DEMO_PROJECT}
        isLocked={false}
        activeTabKey={activeTabKey}
        projectEditAllowed={false}
        editAllowed={!!projectPermissions?.milestones?.project_milestone_edit}
        saveAsTemplateAllowed={false}
        view={view}
        onViewChange={setView}
        onFilterChange={noop}
        onAddProjectType={noopAsyncFalse}
        onUpdateProjectType={noopAsync}
        onDeleteProjectType={noopAsync}
        onReorderProjectTypes={noopAsync}
        onSaveProjectTypeAsTemplate={noop}
      />
      {view === "table" ? (
        <ProjectViewMilestonesList
          persistColumnConfig={false}
          userId={userId}
          showFinanceItems={!!projectPermissions?.planned_finances?.project_planned_finance_view}
          projectPermissions={projectPermissions}
          milestones={milestones}
          dependencies={TOUR_DEMO_DEPENDENCIES}
          editableRow=""
          projectId={TOUR_DEMO_PROJECT_ID}
          contractValue={Number(TOUR_DEMO_PROJECT.sales_price)}
          contractExpenses={Number(TOUR_DEMO_PROJECT.planned_payable)}
          fetching={false}
          count={milestones.length}
          options={{}}
          financeOptions={{}}
          createAllowed={false}
          createDisabled
          selectedIds={[]}
          onSelectedIdsChange={noop}
          onBulkAction={noopBulk}
          onAddMilestone={noop}
          onSelectRow={noop}
          onPageChange={noop}
          onLoadMore={noop}
          onDeleteMilestoneFinanceItem={onDeleteMilestoneFinanceItem}
          onUpdateMilestoneFinanceItem={onUpdateMilestoneFinanceItem}
          onAddMilestoneFinanceItem={onAddMilestoneFinanceItem}
          onReorderMilestones={noop}
        />
      ) : (
        <ProjectViewMilestonesGantt
          milestones={milestones}
          dependencies={TOUR_DEMO_DEPENDENCIES}
          projectName={TOUR_DEMO_PROJECT.lead_name}
        />
      )}
    </div>
  )
}
